import React, { FC, useMemo, useState } from 'react'

import { FieldErrors, UseFieldArrayUpdate } from 'react-hook-form'
import { UseFormSetValue, UseFormWatch } from 'react-hook-form/dist/types/form'

import { TableSortType } from 'src/components/molecules'
import { UserAssignProjectListItem } from 'src/components/organisms'
import { AssignProjectSortItemType } from 'src/components/organisms/projects/AssignProjectListHead'
import {
  UserAssignProjectListHead,
  UserAssignProjectSortItemType,
} from 'src/components/organisms/users/UserAssignProjectListHead'

import { ProjectEntity, UserAssignProjectFormEntity } from 'src/types'
import { CompassRoleEntity } from 'src/types/entities/CompassRole'

type Props = {
  watch: UseFormWatch<UserAssignProjectFormEntity>
  update: UseFieldArrayUpdate<UserAssignProjectFormEntity, 'projects'>
  setValue: UseFormSetValue<UserAssignProjectFormEntity>
  formErrors: FieldErrors<UserAssignProjectFormEntity>
  projects: ProjectEntity[]
  compassRoles: CompassRoleEntity[]
  className?: string
}

export const UserAssignProjectList: FC<Props> = ({
  watch,
  update,
  setValue,
  formErrors,
  projects,
  compassRoles,
  className,
}) => {
  const [sortItem, setSortItem] = useState<UserAssignProjectSortItemType>('status')
  const [sortType, setSortType] = useState<TableSortType>('asc')

  const indexes = new Map(watch('projects').map(({ projectId }, index) => [projectId, index]))

  const filteredProjects = watch('projects').filter(project => !project.isFiltering)

  const sortedWatchedProjects = useMemo(() => {
    const order = sortType === 'desc' ? 1 : -1

    return filteredProjects.sort((a, b) => {
      // まだアサインされていないプロジェクトは常に最上部に表示する
      if (!a.isAlreadyAssigned || !b.isAlreadyAssigned) {
        return 1
      }

      if (sortItem === 'project') {
        return (a.projectName ?? '-') < (b.projectName ?? '-') ? order : -order
      }

      if (sortItem === 'status') {
        const aProject = projects.find(p => p.id === a.projectId)
        const bProject = projects.find(p => p.id === b.projectId)
        return (aProject?.status.priority ?? 0) < (bProject?.status.priority ?? 0) ? order : -order
      }

      if (sortItem === 'role') {
        return (a.compassRolePriority ?? 0) < (b.compassRolePriority ?? 0) ? order : -order
      }

      return 0
    })
  }, [filteredProjects, projects, sortItem, sortType])

  const getTargetProject = (projectId: string) => {
    return projects.find(p => p.id === projectId)
  }

  const handleSelectSortItem = (newItem: AssignProjectSortItemType) => {
    if (sortItem === newItem) {
      const newSortType = sortType === 'desc' ? 'asc' : 'desc'
      setSortType(newSortType)
      return
    }
    setSortItem(newItem)
    setSortType('asc')
  }

  return (
    <div className={className}>
      <UserAssignProjectListHead
        watch={watch}
        setValue={setValue}
        sortType={sortType}
        sortItem={sortItem}
        onSelectItem={handleSelectSortItem}
      />

      {/* 表示可能なプロジェクトが0件の場合は非表示にすることでborder周りのスタイル崩れを防ぐ */}
      {watch('projects').some(p => !p.isDeleting) && (
        // 親コンポーネントでoverflowを用いるとCompass権限用Dropdownも隠れて選択出来ないケースがあるため必要な箇所に角丸を設定する
        <ul className="tw-border-border-secondary tw-border-t tw-bg-surface-primary tw-rounded-b-[var(--s0)]">
          {sortedWatchedProjects.map((project, index) =>
            project.isDeleting ? null : (
              <li
                key={project.projectId ?? ''}
                className="tw-border-b-border-secondary tw-border-b last:tw-border-b-0 tw-py-[0.32rem]"
              >
                <UserAssignProjectListItem
                  project={getTargetProject(project.projectId ?? '')}
                  compassRoles={compassRoles}
                  isAlreadyAssigned={project.isAlreadyAssigned ?? false}
                  isSelected={project.isSelected ?? false}
                  selectedValue={project.compassRoleId}
                  selectOption={v =>
                    update(indexes.get(project.projectId) ?? index, {
                      ...project,
                      compassRoleId: v?.id,
                    })
                  }
                  select={() =>
                    update(indexes.get(project.projectId) ?? index, {
                      ...project,
                      isSelected: !project.isSelected,
                    })
                  }
                  compassRoleErrorMessage={formErrors.projects?.[index]?.compassRoleId?.message}
                />
              </li>
            )
          )}
        </ul>
      )}
    </div>
  )
}
